import { Button, Select, Spin } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import React, { useMemo, useState } from 'react';
import { useTranslations } from '../../hooks/translations';
import { authService, Translate, translationService } from '../../services';
import { Flag } from '@nackle/intl-tools';
import './styles.less';
import WorldFlag from './world_flag.svg';
import { ACTIVE_THEME_NAME } from '../../branding';
import classNames from 'classnames';
import { PaperIcon } from '@nackle/paper';


function getLabel( locale ) {
    return locale.label ?? locale.labelEnUs ?? locale.code ?? '?';
}

function getFlagIcon( locale ) {
    const country = locale.flagCountry ?? ( locale.code ?? locale ).split( '-' )[ 1 ]; // flagCountry only exists on the export codes

    if ( country == 'IW' ) {
        return <Flag code='IL' />;
    }

    if ( country == 'XX' ) {
        return <img src={ WorldFlag } />;
    }
    
    return <Flag code={ country } />;
}

const EXPORT_CODES = [ 
    { code: 'en-XX', label: 'English (Export)', flagCountry: 'US' }, 
    { code: 'fr-MA', label: 'French (Export)', flagCountry: 'FR' }, 
    { code: 'ar-SA', label: 'Arabic (Export)', flagCountry: 'SA' }, 
    { code: 'ru-XX', label: 'Russian (Export)', flagCountry: 'RU' }, 
    { code: 'el-GR', label: 'Greek (Export)', flagCountry: 'GR' }, 
    { code: 'fa-IR', label: 'Farsi (Export)', flagCountry: 'IR' }, 
    { code: 'bg-BG', label: 'Bulgarian (Export)', flagCountry: 'BG' }, 
    { code: 'he-IL', label: 'Hebrew (Export)', flagCountry: 'IL' }, 
    { code: 'lt-LT', label: 'Lithuanian (Export)', flagCountry: 'LT' }, 
    { code: 'ka-GE', label: 'Georgian (Export)', flagCountry: 'GE' }, 
    { code: 'sk-SK', label: 'Slovak (Export)', flagCountry: 'SK' }, 
    { code: 'lv-LV', label: 'Latvian (Export)', flagCountry: 'LV' } 
];

export const LanguageSelector = ( { visible, onCancel } ) => {

    const { setLocale, locale, userLocale, supportedLanguages, isLoading, error } = useTranslations();

    const [ selectedLocale, setSelectedLocale ] = useState( locale );
    const [ buttonLoading, setButtonLoading ] = useState( false );
    const loading = !locale || isLoading;

    const options = useMemo( () => {

        const claims = authService.getClaims();

        const locs = ( claims.locale === 'en-XX' ? EXPORT_CODES : supportedLanguages )
            .map( l => ( { 
                value: l.code, 
                label: l.label ?? l.labelEnUs ?? l.code, 
                icon: getFlagIcon( l )
            } ) )
            .sort( ( a, b ) => a.label.localeCompare( b.label ) )
            .map( l => ( { value: l.value, label: <>{ l.icon } { l.label }</>, className: 'language-selector-option' } ) );

        return [ ...locs.filter( l => [ locale, userLocale ].includes( l.value ) ), ...locs.filter( l => ![ locale, userLocale ].includes( l.value ) ) ];

    }, [ supportedLanguages, locale, userLocale ] );

    async function handleSubmit( ) {
        setButtonLoading( true );
        await setLocale( selectedLocale );
        translationService.invokeManualUpdate( selectedLocale );
        setTimeout( () => location.reload(), 500 );
    }

    function searchFilter( input, option ) {
        return option.value.toLowerCase().includes( input ) || option.children.filter( x => typeof x === 'string' ).some( x => x.toLowerCase().includes( input ) );
    }

    return (
        <Modal title={ <Translate.Text className='_title' value='language.selector.title' /> } destroyOnClose={ true } 
                visible={ visible } onCancel={ onCancel }
                className={ classNames( 'langauge-selector-modal', ACTIVE_THEME_NAME ) }
                footer={ [
                    <Button loading={ loading || buttonLoading  } disabled={ loading } key='ok' type='primary' onClick={ handleSubmit } className='primary' >
                        <Translate.Text value='page.courseDetails.sections.ratings.actions.submit' />
                    </Button>
                ] }>
            <Translate.Text value='language.selector.body' />
            { loading ? <Spin /> :
                <div className='language-selector'>
                    <Select 
                        defaultValue={ locale } 
                        onChange={ ( value ) => setSelectedLocale( value ) } 
                        loading={ isLoading } 
                        disabled={ !!error }
                        showSearch 
                        filterOption={ searchFilter }
                        options={ options }
                    />
                </div>
            }
        </Modal>
    );
};

export const LanguageSelectorLink = ( { onClick: _onClick } ) => {

    const [ isModalVisible, setIsModalVisible ] = useState( false );
    const { locale, supportedLanguages } = useTranslations();

    const showModal = () => {
        setIsModalVisible( true );
    };

    function onClick() {
        showModal();
        _onClick?.();
    }

    const closeModal = () => {
        setIsModalVisible( false );
    };  

    function getShortLabel( label ) {
        let labelSplit;
        if ( ( labelSplit = label.split( ' ' ) ).length > 1 ) {
            return labelSplit[ 0 ];
        }

        return label;
    }

    return supportedLanguages.length == 0 ? (
        <>
            <span className='language-selector-link'>
                <Spin />
            </span>
      </>
    ) : (
        <>
            <span className='language-selector-link'>
                <PaperIcon icon="Global" collection="antd-outlined"/>
                <span className='language'>{ getShortLabel( getLabel( supportedLanguages.find( l => l.code == locale ) ) ) }</span> 
                <PaperIcon icon='Edit' collection='antd-outlined' className='edit-icon' onClick={ onClick } />
            </span>
            <LanguageSelector
                visible={ isModalVisible } onCancel={ closeModal }
            />
      </>
    );
};